import { LegalUnit } from '../../../../../Enums/LegalUnit';
import { PresentationMode } from '../../../../../Enums/PresentationMode';
import { ResponsibleType } from '../../../../../Enums/ResponsibleType';
import { TeamType } from '../../../../../Enums/TeamType';
import {
  doesTeamAlreadyExistAsync,
  getAdUserDetailsAsync,
  isCodePrefixFormatCorrect,
} from '../../../../../Helpers/AgentHelper';
import { hasFieldChangedValue, validateEmail } from '../../../../../Helpers/FormInputHelper';
import { formatMessage } from '../../../../../Localization/formatMessage';
import Messages from '../../../../../Localization/Messages';
import {
  areBrokerFunctionsAvailable,
  areDecoupledFunctionsAvailableFunction,
  getLegalUnit,
  isCommonAgentNameMandatory,
} from '../../../Functions';

export const mapTeamToTeamData = (team, canTeamResponsibleTypeBeSet, modalMode) => {
  return {
    codePrefix: team.codePrefix,
    userId: team.userId,
    commonAgentName: team.commonAgentName,
    bankAccountNo: team.bankAccount?.number,
    bankAccountType: team.bankAccount?.type,
    parentTeamCodePrefix: team.parentTeamCodePrefix,
    name: team.name,
    teamType: team.teamType,
    mobileNo: team.mobileNo,
    email: team.email,
    legalNumber: team.legalNumber,
    employer: team.employer,
    responsibleType:
      modalMode !== PresentationMode.Add && canTeamResponsibleTypeBeSet
        ? team.responsibleType
        : ResponsibleType.Internal,
  };
};

export const mapEditedTeamDataForApi = editedTeamData => {
  if (editedTeamData.parentTeamCodePrefix?.trim() === '' || !editedTeamData.parentTeamCodePrefix) {
    editedTeamData.parentTeamCodePrefix = null;
    editedTeamData.teamType = TeamType.Regular;
  }
  return editedTeamData;
};

export const areFieldsValid = async (teamData, team, modalMode, canTeamNameBeSet) => {
  const errors = {
    commonAgentName: null,
    bankAccountNo: null,
    bankAccountType: null,
    parentTeamCodePrefix: null,
    teamType: null,
    name: null,
    mobileNo: null,
    email: null,
    legalNumber: null,
  };

  if (canTeamNameBeSet) {
    if (!teamData.name?.trim()) {
      errors.name = '';
    }
  } else if (isCommonAgentNameMandatory(team)) {
    if (!teamData.commonAgentName?.trim()) {
      errors.commonAgentName = '';
    }
  }

  if (!!teamData.email && !validateEmail(teamData.email)) {
    errors.email = formatMessage(Messages.common_incorrectEmailFormat);
  }

  if (
    teamData.parentTeamCodePrefix &&
    teamData.parentTeamCodePrefix.trim() !== '' &&
    teamData.teamType === TeamType.Regular
  ) {
    errors.parentTeamCodePrefix = '';
  }

  if (areBrokerFunctionsAvailable(team)) {
    if (isBankAccountMandatory(team)) {
      if (!teamData.bankAccountNo?.trim()) {
        errors.bankAccountNo = '';
      }
      if (!teamData.bankAccountType?.trim()) {
        errors.bankAccountType = '';
      }
    }
  }

  return new Promise(resolve => {
    resolve(errors);
  });
};

export const validateUserIdInput = async userId => {
  const trimmedUserId = userId?.trim();

  if (!userId?.trim()) {
    return null;
  }

  const result = await getAdUserDetailsAsync(trimmedUserId);
  if (!result) {
    return formatMessage(Messages.teamInfo_userIdNotFound);
  }

  return null;
};

export const validateCodePrefixInput = async (codePrefix, modalMode) => {
  if (modalMode !== PresentationMode.Add) {
    return null;
  }

  if (!codePrefix?.trim()) {
    return '';
  }

  if (!isCodePrefixFormatCorrect(codePrefix)) {
    return formatMessage(Messages.common_codePrefixIncorrect);
  }

  const doesTeamExist = await doesTeamAlreadyExistAsync(codePrefix);
  if (doesTeamExist) {
    return formatMessage(Messages.error_CodePrefixExists);
  } else {
    return null;
  }
};

export const canFormBeSubmitted = errors => {
  for (const key of Object.keys(errors)) {
    if (errors[key] !== null) {
      return false;
    }
  }
  return true;
};

export const getChangedFieldsForTeam = (editedTeamData, team) => {
  let sum = 0;
  if (hasFieldChangedValue(editedTeamData?.userId, team?.userId)) {
    sum = sum + 1;
  }
  if (hasFieldChangedValue(editedTeamData?.commonAgentName, team?.commonAgentName)) {
    sum = sum + 2;
  }
  if (
    hasFieldChangedValue(editedTeamData?.bankAccountNo, team?.bankAccount?.number) ||
    hasFieldChangedValue(editedTeamData?.bankAccountType, team?.bankAccount?.type)
  ) {
    sum = sum + 4;
  }
  if (
    hasFieldChangedValue(editedTeamData?.parentTeamCodePrefix, team?.parentTeamCodePrefix) ||
    hasFieldChangedValue(editedTeamData?.teamType, team?.teamType)
  ) {
    sum = sum + 8;
  }
  if (hasFieldChangedValue(editedTeamData?.name, team?.name)) {
    sum = sum + 16;
  }
  if (hasFieldChangedValue(editedTeamData?.mobileNo, team?.mobileNo)) {
    sum = sum + 32;
  }
  if (hasFieldChangedValue(editedTeamData?.email, team?.email)) {
    sum = sum + 64;
  }
  if (hasFieldChangedValue(editedTeamData?.legalNumber, team?.legalNumber)) {
    sum = sum + 128;
  }
  return sum;
};

export const isBankAccountMandatory = team => {
  return (
    !areDecoupledFunctionsAvailableFunction(team) &&
    getLegalUnit(team) !== LegalUnit.Finland &&
    getLegalUnit(team) !== LegalUnit.Denmark
  );
};

export const canEditBankAccount = team => {
  return areBrokerFunctionsAvailable(team);
};

export const getTeamResponsibleTypeOptions = () => {
  return [
    { key: ResponsibleType.Internal, value: ResponsibleType.Internal, displayName: ResponsibleType.Internal },
    { key: ResponsibleType.Broker, value: ResponsibleType.Broker, displayName: ResponsibleType.Broker },
  ];
};
